import React, { useEffect, useRef, useState } from "react";
import api from "../api";
import { Input } from "./ui/input";
import { MapPin } from "lucide-react";

/**
 * Shared address autocomplete input backed by Nominatim (FR only).
 * Calls onSelect({display_name, street, postal_code, city, lat, lon}) when a suggestion is picked.
 * The inner <input> is fully controlled (value / onChange passthrough).
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Commencez à taper une adresse…",
  testId = "addr-autocomplete",
  ...rest
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!value || value.trim().length < 3) { setItems([]); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geocode/autocomplete", { params: { q: value, limit: 5 } });
        setItems(data || []);
        setOpen(true);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (it) => {
    setOpen(false);
    onChange?.(it.street || it.display_name);
    onSelect?.(it);
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testId}>
      <Input
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => items.length && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={`${testId}-input`}
        {...rest}
      />
      {open && (items.length > 0 || loading) && (
        <div className="absolute left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg max-h-72 overflow-auto" style={{ zIndex: 1500 }} data-testid={`${testId}-suggestions`}>
          {loading && <div className="p-3 text-xs text-slate-500">Recherche…</div>}
          {items.map((it, i) => (
            <button
              key={`${it.lat}-${it.lon}-${i}`}
              type="button"
              onClick={() => pick(it)}
              className="w-full text-left p-2.5 border-b last:border-b-0 hover:bg-slate-50 flex items-start gap-2"
              data-testid={`${testId}-item-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 text-sky-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-slate-800">{it.street || it.display_name.split(",")[0]}</div>
                <div className="text-xs text-slate-500">{[it.postal_code, it.city].filter(Boolean).join(" · ") || it.display_name}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
